import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Paper,
  Chip,
  IconButton,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Slider,
  Autocomplete,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Domain as DomainIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import axios from "axios";
import { hostname, parseError, getStripeForAccount } from "../../../utils";
import {
  MAILBOX_ORDER_DOMAIN_PRICE,
  MAILBOX_ORDER_MAILBOX_PRICE,
  MAILBOX_ORDER_MIN_MAILBOXES_PER_DOMAIN,
  MAILBOX_ORDER_MAX_MAILBOXES_PER_DOMAIN,
  MAILBOX_ORDER_EMAILS_PER_DAY_PER_MAILBOX,
} from "../../../constants";
import LoadingButton from "../../../components/LoadingButton";

interface ColdOutreachOrderDialogProps {
  open: boolean;
  onClose: () => void;
  getToken: () => Promise<string>;
  companyId: string;
  onComplete: () => void;
  employees: Array<any>;
  company: any;
  eventListener?: (event: { type: string; message?: string; error?: string }) => void;
}

// Payment form component that uses Stripe Elements
const MailboxPaymentForm = ({
  pricing,
  selectedDomains,
  onSuccess,
  onCancel,
  getToken,
  connectedAccount,
  paymentIntentId,
  companyId,
  referenceDomain,
  contactEmail,
  contactName,
  registrantInfo,
}: {
  pricing: {
    domainCount: number;
    mailboxCount: number;
    domainTotal: number;
    mailboxTotal: number;
    total: number;
    emailsPerDay: number;
  };
  selectedDomains: SelectedDomain[];
  onSuccess: () => void;
  onCancel: () => void;
  getToken: () => Promise<string>;
  connectedAccount?: string;
  paymentIntentId: string;
  companyId: string;
  referenceDomain: string;
  contactEmail: string;
  contactName: string;
  registrantInfo: RegistrantInfo;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError("");

    try {
      // Submit the payment element
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message);
      }

      // Confirm the payment
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      if (confirmError) {
        throw new Error(confirmError.message);
      }

      if (paymentIntent && paymentIntent.status === "succeeded") {
        // Confirm with backend to create order + subscription
        const token = await getToken();

        const orderDetails = {
          domains: selectedDomains.map((d) => ({
            domain: d.domain,
            mailboxes: d.mailboxes.map((m) => ({
              firstName: m.firstName,
              lastName: m.lastName,
              email: m.email,
              employeeId: m.employeeId || null,
            })),
          })),
        };

        const response = await axios.post(
          `${hostname}/mailbox-orders/confirm-payment`,
          {
            paymentIntentId: paymentIntent.id,
            connectedAccount: connectedAccount,
            companyId,
            referenceDomain,
            contactEmail: contactEmail || undefined,
            contactName: contactName || undefined,
            orderDetails,
            registrantInfo,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.success) {
          onSuccess();
        } else {
          throw new Error(response.data.error || "Failed to confirm order");
        }
      }
    } catch (err) {
      setError(parseError(err));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Order Summary</Typography>

        {selectedDomains.map((domain) => (
          <Box key={domain.domain} sx={{ mb: 2 }}>
            <Typography variant="subtitle2">
              <DomainIcon sx={{ mr: 1, verticalAlign: "middle", fontSize: 18 }} />
              {domain.domain}
            </Typography>
            <Box sx={{ pl: 4 }}>
              {domain.mailboxes.map((mailbox, idx) => (
                <Typography key={idx} variant="body2" color="text.secondary">
                  <EmailIcon sx={{ mr: 1, verticalAlign: "middle", fontSize: 16 }} />
                  {mailbox.email} ({mailbox.firstName} {mailbox.lastName})
                </Typography>
              ))}
            </Box>
          </Box>
        ))}

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2">Domains ({pricing.domainCount}) - One-time</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" align="right">${pricing.domainTotal.toFixed(2)}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">Mailboxes ({pricing.mailboxCount}) - Monthly</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" align="right">${pricing.mailboxTotal.toFixed(2)}/mo</Typography>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle1" fontWeight="bold">Due Today</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle1" fontWeight="bold" align="right">
              ${pricing.total.toFixed(2)}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Domain fee:</strong> ${pricing.domainTotal.toFixed(2)} one-time payment (yearly registration)
        </Typography>
        <Typography variant="body2">
          <strong>Mailbox fee:</strong> ${pricing.mailboxTotal.toFixed(2)}/month subscription (starts today)
        </Typography>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Payment Method
        </Typography>
        <PaymentElement
          options={{
            layout: {
              type: "tabs",
              defaultCollapsed: false,
            },
            wallets: {
              applePay: "auto",
              googlePay: "auto",
            },
          }}
        />
      </Paper>

      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
        <Button onClick={onCancel} disabled={processing}>
          Back
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSubmit}
          loading={processing}
          disabled={!stripe || !elements}
        >
          Pay ${pricing.total.toFixed(2)}
        </LoadingButton>
      </Box>
    </Box>
  );
};

interface SelectedDomain {
  domain: string;
  mailboxes: Mailbox[];
}

interface Mailbox {
  firstName: string;
  lastName: string;
  email: string;
  employeeId?: string;
}

interface RegistrantInfo {
  email: string;
  organizationName: string;
  firstName: string;
  lastName: string;
  phone: string;
  jobTitle: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

const COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "GB", name: "United Kingdom" },
  { code: "AU", name: "Australia" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "NL", name: "Netherlands" },
  { code: "ES", name: "Spain" },
  { code: "IT", name: "Italy" },
  { code: "BR", name: "Brazil" },
  { code: "MX", name: "Mexico" },
  { code: "IN", name: "India" },
  { code: "JP", name: "Japan" },
  { code: "SG", name: "Singapore" },
  { code: "NZ", name: "New Zealand" },
  { code: "IE", name: "Ireland" },
  { code: "SE", name: "Sweden" },
  { code: "NO", name: "Norway" },
  { code: "DK", name: "Denmark" },
  { code: "FI", name: "Finland" },
  { code: "CH", name: "Switzerland" },
  { code: "AT", name: "Austria" },
  { code: "BE", name: "Belgium" },
  { code: "PT", name: "Portugal" },
  { code: "PL", name: "Poland" },
];

const emptyRegistrantInfo: RegistrantInfo = {
  email: "",
  organizationName: "",
  firstName: "",
  lastName: "",
  phone: "",
  jobTitle: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  postalCode: "",
  country: "US",
};

const steps = ["Select Domains", "Configure Mailboxes", "Registrant Info", "Confirm & Purchase"];

const ColdOutreachOrderDialog: React.FC<ColdOutreachOrderDialogProps> = ({
  open,
  onClose,
  getToken,
  companyId,
  onComplete,
  employees,
  company,
  eventListener,
}) => {
  const theme = useTheme();
  const mode = theme.palette.mode;

  // Filter out public employees - only show company-owned employees for cold mailboxes
  const availableEmployees = useMemo(() => {
    return employees.filter((e) => !e.public && !e.isPublicEmployee);
  }, [employees]);

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Step 1: Domain selection
  const [referenceDomain, setReferenceDomain] = useState("");
  const [suggestedDomains, setSuggestedDomains] = useState<string[]>([]);
  const [selectedDomains, setSelectedDomains] = useState<SelectedDomain[]>([]);
  const [customDomain, setCustomDomain] = useState("");
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [checkingCustomDomain, setCheckingCustomDomain] = useState(false);
  const [customDomainAvailable, setCustomDomainAvailable] = useState<boolean | null>(null);

  // Step 2: Mailbox configuration (handled in selectedDomains state)

  // Step 3: Registrant information for domain registration
  const [registrantInfo, setRegistrantInfo] = useState<RegistrantInfo>(emptyRegistrantInfo);

  // Step 4: Contact info and payment
  const [contactEmail, setContactEmail] = useState("");
  const [contactName, setContactName] = useState("");

  // Payment state
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [connectedAccount, setConnectedAccount] = useState<string | undefined>(undefined);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [creatingPayment, setCreatingPayment] = useState(false);

  // Get the appropriate Stripe instance
  const stripeInstance = useMemo(() => {
    return getStripeForAccount(connectedAccount);
  }, [connectedAccount]);

  // Helper to clean domain from URL
  const cleanDomain = (url: string): string => {
    if (!url) return "";
    return url
      .replace(/^https?:\/\//, "")
      .replace(/^www\./, "")
      .replace(/\/.*$/, "")
      .toLowerCase()
      .trim();
  };

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setReferenceDomain(cleanDomain(company?.website || ""));
      setSuggestedDomains([]);
      setSelectedDomains([]);
      setCustomDomain("");
      setError("");
      setContactEmail("");
      setContactName("");
      setCustomDomainAvailable(null);
      // Reset registrant info with company defaults
      setRegistrantInfo({
        ...emptyRegistrantInfo,
        organizationName: company?.name || "",
      });
      // Reset payment state
      setClientSecret(null);
      setConnectedAccount(undefined);
      setPaymentIntentId(null);
      setCreatingPayment(false);
    }
  }, [open, company]);

  // Fetch domain suggestions when reference domain changes
  const fetchDomainSuggestions = async () => {
    if (!referenceDomain) return;

    setLoadingSuggestions(true);
    setError("");

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/mailbox-orders/suggest-domains`,
        { domain: referenceDomain },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuggestedDomains(response.data.suggestions || []);
    } catch (err) {
      setError(parseError(err));
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleAddDomain = (domain: string) => {
    if (selectedDomains.find((d) => d.domain === domain)) return;

    // Initialize with one empty mailbox
    setSelectedDomains([
      ...selectedDomains,
      {
        domain,
        mailboxes: [{ firstName: "", lastName: "", email: "" }],
      },
    ]);
  };

  const handleRemoveDomain = (domain: string) => {
    setSelectedDomains(selectedDomains.filter((d) => d.domain !== domain));
  };

  // Helper to check if a domain is a subdomain (has dots before the TLD)
  const isSubdomain = (domain: string): boolean => {
    // Remove .com suffix for checking
    const withoutTld = domain.replace(/\.com$/, "");
    // If there's still a dot, it's a subdomain
    return withoutTld.includes(".");
  };

  const checkCustomDomainAvailability = async () => {
    if (!customDomain) return;

    let domain = customDomain.toLowerCase().trim();
    if (!domain.endsWith(".com")) {
      domain = domain + ".com";
    }

    // Check for subdomains
    if (isSubdomain(domain)) {
      setError("Subdomains are not allowed. Please enter a root domain (e.g., example.com, not sub.example.com)");
      setCustomDomainAvailable(false);
      return;
    }

    setCheckingCustomDomain(true);
    setCustomDomainAvailable(null);
    setError("");

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/mailbox-orders/check-domain`,
        { domain },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCustomDomainAvailable(response.data.available);
      if (!response.data.available) {
        setError(`${domain} is not available`);
      }
    } catch (err) {
      setError(parseError(err));
      setCustomDomainAvailable(false);
    } finally {
      setCheckingCustomDomain(false);
    }
  };

  const handleAddCustomDomain = () => {
    if (!customDomain || !customDomainAvailable) return;
    // Ensure it ends with .com
    let domain = customDomain.toLowerCase().trim();
    if (!domain.endsWith(".com")) {
      domain = domain + ".com";
    }
    handleAddDomain(domain);
    setCustomDomain("");
    setCustomDomainAvailable(null);
  };

  const handleMailboxChange = (
    domainIndex: number,
    mailboxIndex: number,
    field: keyof Mailbox,
    value: string
  ) => {
    const updated = [...selectedDomains];
    updated[domainIndex].mailboxes[mailboxIndex][field] = value;

    // Auto-generate email when first/last name changes
    if (field === "firstName" || field === "lastName") {
      const mailbox = updated[domainIndex].mailboxes[mailboxIndex];
      const domain = updated[domainIndex].domain;
      if (mailbox.firstName && mailbox.lastName) {
        mailbox.email = `${mailbox.firstName.toLowerCase()}.${mailbox.lastName.toLowerCase()}@${domain}`;
      }
    }

    setSelectedDomains(updated);
  };

  const handleAddMailbox = (domainIndex: number) => {
    const updated = [...selectedDomains];
    if (updated[domainIndex].mailboxes.length < MAILBOX_ORDER_MAX_MAILBOXES_PER_DOMAIN) {
      updated[domainIndex].mailboxes.push({ firstName: "", lastName: "", email: "" });
      setSelectedDomains(updated);
    }
  };

  const handleRemoveMailbox = (domainIndex: number, mailboxIndex: number) => {
    const updated = [...selectedDomains];
    if (updated[domainIndex].mailboxes.length > MAILBOX_ORDER_MIN_MAILBOXES_PER_DOMAIN) {
      updated[domainIndex].mailboxes.splice(mailboxIndex, 1);
      setSelectedDomains(updated);
    }
  };

  const handleSelectEmployee = (
    domainIndex: number,
    mailboxIndex: number,
    employee: any | null
  ) => {
    if (!employee) return;
    const updated = [...selectedDomains];
    const domain = updated[domainIndex].domain;
    const names = employee.name.split(" ");
    const firstName = names[0] || "";
    const lastName = names.slice(1).join(" ") || "";

    updated[domainIndex].mailboxes[mailboxIndex] = {
      firstName,
      lastName,
      email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@${domain}`.replace(/\s+/g, ""),
      employeeId: employee.id,
    };
    setSelectedDomains(updated);
  };

  // Calculate pricing
  const calculatePricing = () => {
    const domainCount = selectedDomains.length;
    const mailboxCount = selectedDomains.reduce(
      (sum, d) => sum + d.mailboxes.length,
      0
    );
    const domainTotal = domainCount * MAILBOX_ORDER_DOMAIN_PRICE;
    const mailboxTotal = mailboxCount * MAILBOX_ORDER_MAILBOX_PRICE;
    const total = domainTotal + mailboxTotal;
    const emailsPerDay = mailboxCount * MAILBOX_ORDER_EMAILS_PER_DAY_PER_MAILBOX;

    return { domainCount, mailboxCount, domainTotal, mailboxTotal, total, emailsPerDay };
  };

  const pricing = calculatePricing();

  // Check for duplicate emails across all mailboxes
  const getAllEmails = (): string[] => {
    const emails: string[] = [];
    selectedDomains.forEach((d) => {
      d.mailboxes.forEach((m) => {
        if (m.email) {
          emails.push(m.email.toLowerCase());
        }
      });
    });
    return emails;
  };

  const getDuplicateEmails = (): string[] => {
    const emails = getAllEmails();
    const seen = new Set<string>();
    const duplicates: string[] = [];
    emails.forEach((email) => {
      if (seen.has(email)) {
        if (!duplicates.includes(email)) {
          duplicates.push(email);
        }
      } else {
        seen.add(email);
      }
    });
    return duplicates;
  };

  const duplicateEmails = getDuplicateEmails();
  const hasDuplicateEmails = duplicateEmails.length > 0;

  // Validation
  const isStep1Valid = selectedDomains.length > 0;
  const isStep2Valid = selectedDomains.every(
    (d) =>
      d.mailboxes.length >= MAILBOX_ORDER_MIN_MAILBOXES_PER_DOMAIN &&
      d.mailboxes.every((m) => m.firstName && m.lastName && m.email)
  ) && !hasDuplicateEmails;

  // Step 3: Registrant info validation - all required fields must be filled
  const isStep3Valid = Boolean(
    registrantInfo.email &&
    registrantInfo.email.includes("@") &&
    registrantInfo.organizationName &&
    registrantInfo.firstName &&
    registrantInfo.lastName &&
    registrantInfo.phone &&
    registrantInfo.jobTitle &&
    registrantInfo.address1 &&
    registrantInfo.city &&
    registrantInfo.state &&
    registrantInfo.postalCode &&
    registrantInfo.country
  );

  const isStep4Valid = true; // Payment form handles its own validation

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return isStep1Valid;
      case 1:
        return isStep2Valid;
      case 2:
        return isStep3Valid;
      case 3:
        return isStep4Valid;
      default:
        return false;
    }
  };

  const handleNext = async () => {
    if (activeStep === 2) {
      // Moving from step 3 (registrant info) to step 4 (payment) - create payment intent
      await createPayment();
    } else if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
    }
    // Note: On step 4, payment is handled by the payment form component
  };

  const handleBack = () => {
    if (activeStep === 3) {
      // Going back from payment step - reset payment state
      setClientSecret(null);
      setConnectedAccount(undefined);
      setPaymentIntentId(null);
    }
    setActiveStep((prev) => prev - 1);
  };

  const createPayment = async () => {
    setCreatingPayment(true);
    setError("");

    try {
      const token = await getToken();

      const orderDetails = {
        domains: selectedDomains.map((d) => ({
          domain: d.domain,
          mailboxes: d.mailboxes.map((m) => ({
            firstName: m.firstName,
            lastName: m.lastName,
            email: m.email,
            employeeId: m.employeeId || null,
          })),
        })),
      };

      const response = await axios.post(
        `${hostname}/mailbox-orders/create-payment`,
        {
          companyId,
          referenceDomain,
          contactEmail: contactEmail || undefined,
          contactName: contactName || undefined,
          orderDetails,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setClientSecret(response.data.clientSecret);
      setConnectedAccount(response.data.connectedAccount);
      setPaymentIntentId(response.data.paymentIntentId);
      setActiveStep(3);
    } catch (err) {
      setError(parseError(err));
    } finally {
      setCreatingPayment(false);
    }
  };

  const handlePaymentSuccess = () => {
    if (eventListener) {
      eventListener({
        type: "SET_SUCCESS",
        message: "Order placed successfully! Your domains and mailboxes will be ready within 24-48 hours.",
      });
    }
    onComplete();
    onClose();
  };

  const renderStep1 = () => (
    <Box>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Enter your current domain to get similar .com domain suggestions for cold outreach.
      </Typography>

      <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "flex-start" }}>
        <TextField
          label="Your Current Domain"
          value={referenceDomain}
          onChange={(e) => setReferenceDomain(e.target.value)}
          placeholder="acme.com"
          fullWidth
        />
        <LoadingButton
          variant="contained"
          onClick={fetchDomainSuggestions}
          loading={loadingSuggestions}
          disabled={!referenceDomain}
          sx={{ minWidth: 100, height: 56 }}
        >
          Suggest
        </LoadingButton>
      </Box>

      {suggestedDomains.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Available Domains (click to select):
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {suggestedDomains.map((domain) => {
              const isSelected = selectedDomains.find((d) => d.domain === domain);
              return (
                <Box
                  key={domain}
                  onClick={() => isSelected ? handleRemoveDomain(domain) : handleAddDomain(domain)}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 1.5,
                    borderRadius: 1,
                    border: isSelected ? "2px solid" : "1px solid",
                    borderColor: isSelected ? "primary.main" : "divider",
                    bgcolor: "background.paper",
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: isSelected ? 600 : 400,
                    }}
                  >
                    {domain}
                  </Typography>
                  <Chip
                    label="Available"
                    size="small"
                    icon={<CheckIcon />}
                    color="primary"
                    sx={{ ml: 2 }}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Add Custom Domain
      </Typography>
      <Box sx={{ display: "flex", gap: 1, mb: 2, alignItems: "flex-start" }}>
        <TextField
          value={customDomain}
          onChange={(e) => {
            setCustomDomain(e.target.value);
            setCustomDomainAvailable(null);
          }}
          placeholder="customdomain.com"
          fullWidth
          size="small"
          helperText="Only .com domains are available"
        />
        <LoadingButton
          variant="outlined"
          onClick={checkCustomDomainAvailability}
          loading={checkingCustomDomain}
          disabled={!customDomain}
          sx={{ minWidth: 80, height: 40 }}
        >
          Check
        </LoadingButton>
        {customDomainAvailable && (
          <Button
            variant="contained"
            onClick={handleAddCustomDomain}
            sx={{ minWidth: 80, height: 40 }}
          >
            Add
          </Button>
        )}
      </Box>
      {customDomainAvailable === true && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Domain is available! Click Add to select it.
        </Alert>
      )}
      {customDomainAvailable === false && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Domain is not available. Try a different name.
        </Alert>
      )}

      {selectedDomains.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Selected Domains ({selectedDomains.length}):
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {selectedDomains.map((d) => (
              <Box
                key={d.domain}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 1.5,
                  borderRadius: 1,
                  border: "2px solid",
                  borderColor: "primary.main",
                  bgcolor: "background.paper",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <DomainIcon fontSize="small" color="primary" />
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {d.domain}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveDomain(d.domain)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <Paper sx={{ p: 2, mt: 3, bgcolor: "action.hover" }}>
        <Typography variant="subtitle2">Pricing Summary</Typography>
        <Typography variant="body2">
          {pricing.domainCount} domain(s) x ${MAILBOX_ORDER_DOMAIN_PRICE} = ${pricing.domainTotal.toFixed(2)}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Mailboxes will be configured in the next step ($
          {MAILBOX_ORDER_MAILBOX_PRICE}/mailbox)
        </Typography>
      </Paper>
    </Box>
  );

  const [autoFillCount, setAutoFillCount] = useState(2);

  const handleAutoFill = () => {
    if (availableEmployees.length === 0) return;

    const updated = selectedDomains.map((domain) => {
      const mailboxes: Mailbox[] = [];
      for (let i = 0; i < autoFillCount; i++) {
        const employee = availableEmployees[i % availableEmployees.length];
        const names = (employee.name || "").split(" ");
        const firstName = names[0] || "";
        const lastName = names.slice(1).join(" ") || "";
        mailboxes.push({
          firstName,
          lastName,
          email: `${firstName.toLowerCase()}.${lastName.toLowerCase().replace(/\s+/g, "")}@${domain.domain}`,
          employeeId: employee.id,
        });
      }
      return { ...domain, mailboxes };
    });
    setSelectedDomains(updated);
  };

  const renderStep2 = () => (
    <Box>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Configure mailboxes for each domain. You can select names from your AI Employees or enter custom names.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Quick Setup
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Mailboxes</InputLabel>
            <Select
              value={autoFillCount}
              label="Mailboxes"
              onChange={(e) => setAutoFillCount(Number(e.target.value))}
            >
              {[1, 2, 3, 4].map((n) => (
                <MenuItem key={n} value={n}>
                  {n} per domain
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={handleAutoFill}
            disabled={availableEmployees.length === 0}
          >
            Auto Fill
          </Button>
          {availableEmployees.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No AI Employees available
            </Typography>
          )}
        </Box>
      </Paper>

      <Alert severity="info" sx={{ mb: 3 }}>
        Each mailbox can send up to {MAILBOX_ORDER_EMAILS_PER_DAY_PER_MAILBOX} emails per day.
        With {pricing.mailboxCount} mailbox(es), you can send up to{" "}
        <strong>{pricing.emailsPerDay} emails per day</strong>.
      </Alert>

      {hasDuplicateEmails && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Duplicate email addresses found: {duplicateEmails.join(", ")}. Each mailbox must have a unique email address.
        </Alert>
      )}

      {selectedDomains.map((domain, domainIndex) => (
        <Card key={domain.domain} variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6">
                <DomainIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                {domain.domain}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {domain.mailboxes.length}/{MAILBOX_ORDER_MAX_MAILBOXES_PER_DOMAIN} mailboxes
              </Typography>
            </Box>

            {domain.mailboxes.map((mailbox, mailboxIndex) => (
              <Box key={mailboxIndex} sx={{ mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <Autocomplete
                      options={availableEmployees}
                      getOptionLabel={(option: any) => option.name || ""}
                      onChange={(_, value) =>
                        handleSelectEmployee(domainIndex, mailboxIndex, value)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select AI Employee"
                          size="small"
                        />
                      )}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      label="First Name"
                      value={mailbox.firstName}
                      onChange={(e) =>
                        handleMailboxChange(domainIndex, mailboxIndex, "firstName", e.target.value)
                      }
                      size="small"
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      label="Last Name"
                      value={mailbox.lastName}
                      onChange={(e) =>
                        handleMailboxChange(domainIndex, mailboxIndex, "lastName", e.target.value)
                      }
                      size="small"
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Email Address"
                      value={mailbox.email}
                      onChange={(e) =>
                        handleMailboxChange(domainIndex, mailboxIndex, "email", e.target.value)
                      }
                      size="small"
                      fullWidth
                      required
                      InputProps={{
                        startAdornment: <EmailIcon sx={{ mr: 1, color: "action.active" }} />,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <IconButton
                      onClick={() => handleRemoveMailbox(domainIndex, mailboxIndex)}
                      disabled={domain.mailboxes.length <= MAILBOX_ORDER_MIN_MAILBOXES_PER_DOMAIN}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>
            ))}

            {domain.mailboxes.length < MAILBOX_ORDER_MAX_MAILBOXES_PER_DOMAIN && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => handleAddMailbox(domainIndex)}
                size="small"
              >
                Add Mailbox
              </Button>
            )}
          </CardContent>
        </Card>
      ))}

      <Paper sx={{ p: 2, bgcolor: "action.hover" }}>
        <Typography variant="subtitle2">Pricing Summary</Typography>
        <Typography variant="body2">
          {pricing.domainCount} domain(s) x ${MAILBOX_ORDER_DOMAIN_PRICE} = ${pricing.domainTotal.toFixed(2)}
        </Typography>
        <Typography variant="body2">
          {pricing.mailboxCount} mailbox(es) x ${MAILBOX_ORDER_MAILBOX_PRICE} = ${pricing.mailboxTotal.toFixed(2)}
        </Typography>
        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle1">
          Total: ${pricing.total.toFixed(2)}
        </Typography>
      </Paper>
    </Box>
  );

  const handleRegistrantChange = (field: keyof RegistrantInfo, value: string) => {
    setRegistrantInfo((prev) => ({ ...prev, [field]: value }));
  };

  const renderStep3 = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Important:</strong> Please provide accurate contact information. A verification email will be sent to confirm the domain registration.
        </Typography>
      </Alert>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Email"
            value={registrantInfo.email}
            onChange={(e) => handleRegistrantChange("email", e.target.value)}
            required
            type="email"
            helperText="A verification email will be sent to this address"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Organization Name"
            value={registrantInfo.organizationName}
            onChange={(e) => handleRegistrantChange("organizationName", e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="First Name"
            value={registrantInfo.firstName}
            onChange={(e) => handleRegistrantChange("firstName", e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Last Name"
            value={registrantInfo.lastName}
            onChange={(e) => handleRegistrantChange("lastName", e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Phone"
            value={registrantInfo.phone}
            onChange={(e) => handleRegistrantChange("phone", e.target.value)}
            required
            placeholder="+1 555-123-4567"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Job Title"
            value={registrantInfo.jobTitle}
            onChange={(e) => handleRegistrantChange("jobTitle", e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address Line 1"
            value={registrantInfo.address1}
            onChange={(e) => handleRegistrantChange("address1", e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address Line 2"
            value={registrantInfo.address2}
            onChange={(e) => handleRegistrantChange("address2", e.target.value)}
            placeholder="Suite, Apt, etc. (optional)"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="City"
            value={registrantInfo.city}
            onChange={(e) => handleRegistrantChange("city", e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="State / Region"
            value={registrantInfo.state}
            onChange={(e) => handleRegistrantChange("state", e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Postal Code"
            value={registrantInfo.postalCode}
            onChange={(e) => handleRegistrantChange("postalCode", e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel>Country</InputLabel>
            <Select
              value={registrantInfo.country}
              label="Country"
              onChange={(e) => handleRegistrantChange("country", e.target.value)}
            >
              {COUNTRIES.map((country) => (
                <MenuItem key={country.code} value={country.code}>
                  {country.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );

  const renderStep4 = () => {
    if (!clientSecret || !paymentIntentId) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Elements
        stripe={stripeInstance}
        options={{
          clientSecret,
          appearance: {
            theme: mode === "dark" ? "night" : "stripe",
          },
        }}
      >
        <MailboxPaymentForm
          pricing={pricing}
          selectedDomains={selectedDomains}
          onSuccess={handlePaymentSuccess}
          onCancel={handleBack}
          getToken={getToken}
          connectedAccount={connectedAccount}
          companyId={companyId}
          referenceDomain={referenceDomain}
          contactEmail={contactEmail}
          contactName={contactName}
          paymentIntentId={paymentIntentId}
          registrantInfo={registrantInfo}
        />
      </Elements>
    );
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderStep1();
      case 1:
        return renderStep2();
      case 2:
        return renderStep3();
      case 3:
        return renderStep4();
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        Order Cold Outreach Mailboxes
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 4, mt: 1 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {renderStepContent()}
      </DialogContent>

      {/* Hide dialog actions on step 4 (payment) - payment form has its own buttons */}
      {activeStep < 3 && (
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          {activeStep > 0 && (
            <Button onClick={handleBack} disabled={loading || creatingPayment}>
              Back
            </Button>
          )}
          <LoadingButton
            variant="contained"
            onClick={handleNext}
            loading={loading || creatingPayment}
            disabled={!canProceed()}
          >
            Next
          </LoadingButton>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ColdOutreachOrderDialog;
